/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { captureException } from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Modal } from '@blend/shared-react';
import { mixins, colors } from '@blend/consumer-components';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { addApiErrorNotification } from '../../../../../actions/pageActions';
import { notaryAPI } from '../../../../../api/notary';
import { CommonAPI } from '../../../../../api/common/responses';
import { getStyleConfig, styles as modalStyles } from '../../../../../components/common/Modals/styles';
import { NotaryAPI } from '../../../../../api/notary/types';
import { useRootSelector } from '../../../../../hooks/useRootSelector';
import { ActionButton } from '@components/ActionButton/ActionButton';

interface ReviewUserModal {
  modalOpen: boolean;
  setModalOpen(modalOpen: boolean): void;
  user: CommonAPI.User;
}

const styles = StyleSheet.create({
  ...modalStyles,
  content: {
    ...modalStyles.content,
    padding: '10px',
  },
  heading: {
    ...modalStyles.heading,
    textAlign: 'center',
    marginBottom: 5,
  },
  wrapper: {
    marginTop: '-10px',
  },
  warning: {
    color: 'darkred',
  },
  idInformation: {
    padding: '10px 0',
  },
});

const overlayStyleConfig = {
  backgroundColor: mixins.rgba(0, 0, 0, 0.0),
  top: '230px',
};

export const ReviewUserModal: React.FC<ReviewUserModal> = ({ modalOpen, setModalOpen, user }) => {
  const { firstName, lastName, participantSessionId } = user;
  const [reviewData, setReviewData] = useState<NotaryAPI.ParticipantSessionForReviewResponse>();
  const isMobileView = useRootSelector(state => state.layout.isMobileView);
  const dispatch = useDispatch();

  useEffect(() => {
    async function fetchReviewData() {
      if (participantSessionId) {
        try {
          const reviewDataResult = await notaryAPI.calls.getParticipantSessionDetailsForReview(participantSessionId);
          setReviewData(reviewDataResult);
        } catch (ex) {
          captureException(ex);
          setModalOpen(false);
          addApiErrorNotification('Failed to load review data', ex as AxiosError<{ message?: string }>)(dispatch);
        }
      }
    }

    fetchReviewData();
  }, []);

  const dateOfBirthMismatch =
    reviewData?.dateOfBirthFromLender && reviewData.dateOfBirth !== reviewData?.dateOfBirthFromLender;

  return (
    <div className={css(styles.wrapper)}>
      {reviewData && (
        <Modal
          overlayStyleConfig={overlayStyleConfig}
          styleConfig={{ ...getStyleConfig(isMobileView), backgroundColor: colors.white, boxShadow: 'none' }}
          isOpen={modalOpen}
          handleClose={() => setModalOpen(false)}
          contentLabel={'Review Photo ID'}
          data-it={`ReviewUserModal-${user.participantSessionId}`}
        >
          <div className={css(styles.content)}>
            <h1 className={css(styles.heading)}>Review Photo ID</h1>
            <h2 className={css(styles.heading)}>
              {firstName} {lastName}
            </h2>
            <img width="500" src={`${reviewData.identityDocumentImgTags[0]}`} alt="document image front side" />
            {reviewData.identityDocumentImgTags.length === 2 && (
              <img width="500" src={`${reviewData.identityDocumentImgTags[1]}`} alt="document image back side" />
            )}
            <h2 className={css(styles.heading)}>ID Information</h2>
            <div className={css(styles.idInformation)}>
              {dateOfBirthMismatch && (
                <div data-it="ReviewUserModal-dobMismatch" className={css(styles.warning)}>
                  <span>Date of birth does not match information from lender ({reviewData.dateOfBirthFromLender})</span>
                </div>
              )}
              {reviewData.idvReasons?.length ? (
                <span className={css(styles.warning)} data-it="idvReasons">
                  IDV error reasons - {reviewData.idvReasons.join(' ')}
                </span>
              ) : undefined}
              <br />
              <span>Date of Birth - {reviewData.dateOfBirth}</span>
              <br />
              <span>Issuance Date - {reviewData.issuanceDate}</span>
              <br />
              <span>Expiration Date - {reviewData.expirationDate}</span>
              <br />
              <span>Credential Number - {reviewData.credentialNumber}</span>
              <br />
            </div>
            <div className={css(styles.primaryButton)}>
              <ActionButton data-dd-privacy="allow" onClick={() => setModalOpen(false)}>
                Done
              </ActionButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
